import { useState, useEffect } from 'react';
import BackButton from '../components/BackButton';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function Security() {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [token, setToken] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('check');

  useEffect(() => {
    checkStatus();
  }, []);

  async function checkStatus() {
    try {
      const res = await api.get('/2fa/status');
      setStatus(res.data);
      setStep(res.data.enabled ? 'done' : 'check');
    } catch (e) {
      setError(e.userMessage);
    } finally {
      setLoading(false);
    }
  }

  async function startSetup() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/2fa/setup');
      setSetupData(res.data);
      setStep('setup');
    } catch (e) {
      setError(e.userMessage);
    } finally {
      setLoading(false);
    }
  }

  async function enableTwoFA() {
    if (token.length !== 6) {
      setError(t('security.enter_6digit'));
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/2fa/enable', { token });
      setBackupCodes(res.data.backup_codes);
      setStep('done');
      setStatus({ enabled: true });
    } catch (e) {
      setError(e.userMessage);
    } finally {
      setLoading(false);
    }
  }

  async function disableTwoFA() {
    if (!token) {
      setError(t('security.enter_current'));
      return;
    }
    
    if (!confirm(t('security.confirm_disable'))) return;
    
    setLoading(true);
    setError(null);
    try {
      await api.post('/2fa/disable', { token });
      setStatus({ enabled: false });
      setStep('check');
      setToken('');
      setSetupData(null);
      setBackupCodes(null);
    } catch (e) {
      setError(e.userMessage);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page">
      <BackButton />
      <h1>🔐 {t('security.title')}</h1>

      {loading && <div className="loader">{t('common.loading')}</div>}
      
      {error && (
        <div style={{
          background: 'rgba(255,107,107,0.2)',
          border: '1px solid rgba(255,107,107,0.5)',
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
          color: '#ff6b6b',
        }}>
          ❌ {error}
        </div>
      )}

      {step === 'check' && !loading && (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🔓</div>
          <h2>{t('security.2fa_title')}</h2>
          <p style={{ opacity: 0.7, marginBottom: 24 }}>
            {t('security.2fa_desc')}
          </p>
          <button className="btn btn-primary" onClick={startSetup}>
            🔐 {t('security.enable_2fa')}
          </button>
        </div>
      )}

      {step === 'setup' && setupData && (
        <div>
          <h3>{t('security.step1')}</h3>
          <p style={{ opacity: 0.7, fontSize: 14 }}>
            {t('security.step1_desc')}
          </p>
          
          {setupData.qr_code ? (
            <div style={{ textAlign: 'center', margin: '20px 0' }}>
              <img 
                src={setupData.qr_code} 
                alt="QR Code" 
                style={{ maxWidth: 200, borderRadius: 8 }}
              />
            </div>
          ) : (
            <div style={{
              background: 'rgba(0,0,0,0.3)',
              padding: 16,
              borderRadius: 8,
              marginBottom: 16,
              fontFamily: 'monospace',
              wordBreak: 'break-all',
            }}>
              <strong>{t('security.secret_key')}:</strong><br/>
              {setupData.secret}
            </div>
          )}

          <h3>{t('security.step2')}</h3>
          <input
            type="text"
            value={token}
            onChange={e => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            style={{
              width: '100%',
              padding: 16,
              fontSize: 24,
              textAlign: 'center',
              letterSpacing: 8,
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 8,
              color: 'white',
              marginBottom: 16,
            }}
          />
          
          <button 
            className="btn btn-primary" 
            onClick={enableTwoFA}
            disabled={token.length !== 6 || loading}
            style={{ width: '100%' }}
          >
            ✅ {t('security.confirm_enable')}
          </button>
        </div>
      )}

      {step === 'done' && (
        <div>
          {backupCodes ? (
            <>
              <div style={{
                background: 'rgba(0,206,201,0.2)',
                border: '1px solid rgba(0,206,201,0.5)',
                padding: 16,
                borderRadius: 8,
                marginBottom: 16,
              }}>
                <h3 style={{ margin: '0 0 8px 0' }}>✅ {t('security.2fa_enabled')}</h3>
                <p style={{ margin: 0, opacity: 0.8 }}>
                  {t('security.save_backup')}
                </p>
              </div>
              
              <h3>🔑 {t('security.backup_codes')}</h3>
              <p style={{ fontSize: 13, opacity: 0.7 }}>
                {t('security.backup_desc')}
              </p>
              
              <div style={{
                background: 'rgba(0,0,0,0.3)',
                padding: 16,
                borderRadius: 8,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 8,
                fontFamily: 'monospace',
                marginBottom: 16,
              }}>
                {backupCodes.map((code, i) => (
                  <div key={i} style={{ padding: 8, background: 'rgba(0,0,0,0.2)', borderRadius: 4 }}>
                    {code}
                  </div>
                ))}
              </div>
              
              <button 
                className="btn btn-secondary"
                onClick={() => setBackupCodes(null)}
                style={{ width: '100%' }}
              >
                {t('common.done')}
              </button>
            </>
          ) : (
            <>
              <div style={{
                background: 'rgba(0,206,201,0.15)',
                borderRadius: 12,
                padding: 24,
                textAlign: 'center',
                marginBottom: 24,
              }}>
                <div style={{ fontSize: 48 }}>🔒</div>
                <h2 style={{ margin: '16px 0 8px 0' }}>{t('security.2fa_active')}</h2>
                <p style={{ margin: 0, opacity: 0.7 }}>
                  {t('security.2fa_protected')}
                </p>
              </div>

              <h3>{t('security.disable_2fa')}</h3>
              <p style={{ fontSize: 13, opacity: 0.7 }}>
                {t('security.disable_desc')}
              </p>
              
              <input
                type="text"
                value={token}
                onChange={e => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                style={{
                  width: '100%',
                  padding: 12,
                  fontSize: 18,
                  textAlign: 'center',
                  letterSpacing: 4,
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 8,
                  color: 'white',
                  marginBottom: 12,
                }}
              />
              
              <button 
                className="btn"
                onClick={disableTwoFA}
                disabled={!token || loading}
                style={{ 
                  width: '100%',
                  background: 'rgba(255,107,107,0.3)',
                  border: '1px solid rgba(255,107,107,0.5)',
                }}
              >
                🔓 {t('security.disable_2fa')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
